import { createContext, useContext, useEffect, useState, useCallback } from "react";

const CartContext = createContext(null);
const STORAGE_KEY = "bu_cart";

const load = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
};

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(load);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  const add = useCallback((product, size, color, quantity = 1, personalization = "", personalizationImage = "") => {
    const maxQty = product.track_inventory ? Math.max(0, product.stock || 0) : 100;
    const persText = (personalization || "").trim();
    const persImg = (personalizationImage || "").trim();
    const persFee = product.personalization && (persText || persImg) ? (product.personalization_fee || 0) : 0;
    const sizeUp = Number(product.size_upcharges?.[size] || 0);
    const unitPrice = (product.price || 0) + persFee + sizeUp;
    setItems((prev) => {
      const lineId = `${product.id}|${size || ""}|${color || ""}|${persText}|${persImg}`;
      const existing = prev.find((i) => i.lineId === lineId);
      if (existing) {
        return prev.map((i) => (i.lineId === lineId ? { ...i, maxQty, quantity: Math.min(maxQty, i.quantity + quantity) } : i));
      }
      return [
        ...prev,
        {
          lineId,
          productId: product.id,
          name: product.name,
          img: product.img,
          price: product.price,
          unitPrice,
          size: size || "",
          color: color || "",
          sizeUpcharge: sizeUp,
          personalization: persText,
          personalizationImage: persImg,
          personalizationFee: persFee,
          fulfillment: product.fulfillment,
          ship_fee: product.ship_fee || 0,
          maxQty,
          quantity: Math.min(maxQty, quantity),
        },
      ];
    });
    setOpen(true);
  }, []);

  const setQty = useCallback((lineId, quantity) => {
    setItems((prev) => prev.map((i) => (i.lineId === lineId ? { ...i, quantity: Math.max(1, Math.min(i.maxQty ?? 100, quantity)) } : i)));
  }, []);

  const remove = useCallback((lineId) => {
    setItems((prev) => prev.filter((i) => i.lineId !== lineId));
  }, []);

  const clear = useCallback(() => setItems([]), []);

  const count = items.reduce((n, i) => n + i.quantity, 0);
  const subtotal = items.reduce((s, i) => s + (i.unitPrice ?? i.price) * i.quantity, 0);
  const shipping = items
    .filter((i) => i.fulfillment === "ship")
    .reduce((m, i) => Math.max(m, i.ship_fee || 0), 0);

  return (
    <CartContext.Provider value={{ items, open, setOpen, add, setQty, remove, clear, count, subtotal, shipping }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => useContext(CartContext);
